/**
 * The set of locales this app ships translations for, shared so the
 * frontend, the API and their zod schemas can never disagree about what a
 * valid locale string is.
 *
 * These are BCP-47 tags: they go into `<html lang>`, into `Intl.*`
 * constructors on both sides, and into the `Accept-Language`-style matching
 * in `frontend/src/i18n`. Adding one here is step one of adding a language —
 * see the i18n section in AGENTS.md for the rest.
 */
export const SUPPORTED_LOCALES = ["en", "fr", "de", "es", "pl", "zh-Hans"];
/**
 * The locale used when nothing better is known: no `DEFAULT_LOCALE` set by
 * the operator, no stored choice, and nothing in the browser's language
 * list we ship a translation for.
 */
export const FALLBACK_LOCALE = "en";
export function isSupportedLocale(value) {
    return typeof value === "string" && SUPPORTED_LOCALES.includes(value);
}
/**
 * Best match for one language tag, by the usual narrowing rules: an exact
 * hit first, then the base language (`fr-CA` → `fr`), then a script-less
 * request for a language we only ship with a script (`zh` → `zh-Hans`).
 * Returns undefined rather than a fallback so callers can keep walking a
 * preference list — see `resolveLocale`.
 */
export function matchLocale(tag) {
    const normalized = tag.trim().toLowerCase();
    if (!normalized)
        return undefined;
    const exact = SUPPORTED_LOCALES.find((locale) => locale.toLowerCase() === normalized);
    if (exact)
        return exact;
    const base = normalized.split("-")[0];
    return SUPPORTED_LOCALES.find((locale) => locale.toLowerCase().split("-")[0] === base);
}
/**
 * First supported locale among the caller's preferences, in order. Takes
 * anything nullable so callers can pass `navigator.languages`, a stored
 * value and an operator default in one list without pre-filtering.
 */
export function resolveLocale(...preferences) {
    for (const preference of preferences) {
        if (!preference)
            continue;
        const match = matchLocale(preference);
        if (match)
            return match;
    }
    return FALLBACK_LOCALE;
}
//# sourceMappingURL=locales.js.map